import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import Section from '../components/common/Section';
import styled from 'styled-components';

interface PostSummary {
  id: number;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  created_at: string;
}

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled(Link)`
  background-color: var(--background-light);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const BlogImage = styled.div`
  height: 200px;
  overflow: hidden;
  background-color: var(--background-dark);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;

    ${BlogCard}:hover & {
      transform: scale(1.05);
    }
  }
`;

const BlogContent = styled.div`
  padding: 1.5rem;
`;

const BlogDate = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const BlogTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const BlogExcerpt = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const Empty = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
`;

function formatDate(iso: string): string {
  try {
    return new Date(iso.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const BlogPage: React.FC = () => {
  const location = useLocation();
  const isSpecificBlogPost = location.pathname.startsWith('/blog/');
  const canonicalUrl = isSpecificBlogPost ? 'https://flashusdtsoftwareshop.com/blog' : undefined;
  const [posts, setPosts] = useState<PostSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/posts')
      .then((r) => r.json())
      .then((data) => setPosts(data.posts || []))
      .catch(() => setError('Could not load posts'));
  }, []);

  return (
    <Layout>
      <SEO
        title="USDT Flashing Binance | Blog Flash USDT Software Shop"
        description="Stay updated with the latest news, tips, and insights about USDT flashing on Binance and other cryptocurrency platforms."
        keywords="USDT flashing binance, USDT flasher blog, cryptocurrency blog, blockchain news, USDT flashing tips, crypto guides"
        canonicalUrl={canonicalUrl}
      />

      <Section
        title="Flash USDT Software Shop Blog"
        subtitle="Stay updated with the latest news, tips, and insights about USDT flashing and cryptocurrency trends."
        background="light"
        padding="large"
        isMainTitle={true}
      >
        {error && <Empty>{error}</Empty>}
        {!error && posts === null && <Empty>Loading…</Empty>}
        {!error && posts !== null && posts.length === 0 && (
          <Empty>No posts yet. Check back soon.</Empty>
        )}
        {!error && posts !== null && posts.length > 0 && (
          <BlogGrid>
            {posts.map((post) => (
              <BlogCard key={post.id} to={`/blog/${post.slug}`}>
                <BlogImage>
                  {post.cover_image_url && (
                    <img src={post.cover_image_url} alt={post.title} />
                  )}
                </BlogImage>
                <BlogContent>
                  <BlogDate>{formatDate(post.created_at)}</BlogDate>
                  <BlogTitle>{post.title}</BlogTitle>
                  {post.excerpt && <BlogExcerpt>{post.excerpt}</BlogExcerpt>}
                </BlogContent>
              </BlogCard>
            ))}
          </BlogGrid>
        )}
      </Section>
    </Layout>
  );
};

export default BlogPage;
